import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderOpen, Calendar, Star, Users, ArrowRight, Clock, CheckCircle, AlertCircle, Server, Mail, CreditCard } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, healthRes, emailRes] = await Promise.all([
          api.get('/admin/stats'),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`).then(r => r.json()).catch(() => null),
          api.get('/admin/email-logs/stats').catch(() => null)
        ]);
        setStats(statsRes.data);
        setHealth(healthRes);
        setEmailStats(emailRes?.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    {
      title: 'Progetti',
      value: stats?.projects?.total || 0,
      subtitle: `${stats?.projects?.published || 0} pubblicati`,
      icon: FolderOpen,
      href: '/admin/progetti',
      color: 'text-blue-500'
    },
    {
      title: 'Prenotazioni',
      value: stats?.bookings?.total || 0,
      subtitle: `${stats?.bookings?.pending || 0} in attesa`,
      icon: Calendar,
      href: '/admin/prenotazioni',
      color: 'text-green-500'
    },
    {
      title: 'Recensioni',
      value: stats?.reviews?.total || 0,
      subtitle: `${stats?.reviews?.pending || 0} da moderare`,
      icon: Star,
      href: '/admin/recensioni',
      color: 'text-yellow-500'
    },
    {
      title: 'Lead',
      value: stats?.leads?.total || 0,
      subtitle: `${stats?.leads?.new || 0} nuovi`,
      icon: Users,
      href: '/admin/leads',
      color: 'text-purple-500'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Panoramica della tua attività</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link to={stat.href}>
              <Card className="hover:border-foreground/50 transition-colors" data-testid={`stat-card-${stat.title.toLowerCase()}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bookings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Prenotazioni Recenti</CardTitle>
            <Link to="/admin/prenotazioni">
              <Button variant="ghost" size="sm">
                Vedi tutte
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_bookings?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-sm"
                    data-testid={`recent-booking-${booking.id}`}
                  >
                    <div>
                      <p className="font-medium">{booking.first_name} {booking.last_name}</p>
                      <p className="text-sm text-muted-foreground">{booking.service_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{booking.preferred_date}</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        booking.status === 'confermata' ? 'bg-green-100 text-green-700' :
                        booking.status === 'nuova' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Nessuna prenotazione recente</p>
            )}
          </CardContent>
        </Card>

        {/* Pending Reviews */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recensioni in Attesa</CardTitle>
            <Link to="/admin/recensioni">
              <Button variant="ghost" size="sm">
                Vedi tutte
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.pending_reviews?.length > 0 ? (
              <div className="space-y-4">
                {stats.pending_reviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-3 bg-muted/50 rounded-sm"
                    data-testid={`pending-review-${review.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{review.name}</p>
                      <div className="flex text-yellow-500">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} size={14} fill="currentColor" />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{review.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Nessuna recensione in attesa</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Azioni Rapide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/progetti">
              <Button variant="outline" data-testid="quick-action-projects">
                <FolderOpen size={16} className="mr-2" />
                Nuovo Progetto
              </Button>
            </Link>
            <Link to="/admin/disponibilita">
              <Button variant="outline" data-testid="quick-action-availability">
                <Clock size={16} className="mr-2" />
                Gestisci Disponibilità
              </Button>
            </Link>
            <Link to="/admin/impostazioni">
              <Button variant="outline" data-testid="quick-action-settings">
                Impostazioni Sito
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* System Status */}
      <Card className="mt-6" data-testid="system-status">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Server size={20} />
            System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Database */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
              {health?.database?.status === 'ok' ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <AlertCircle className="text-red-500" size={20} />
              )}
              <div>
                <p className="text-sm font-medium">Database</p>
                <p className="text-xs text-muted-foreground">
                  {health?.database?.status === 'ok' ? 'Connesso' : health?.database?.error || 'Errore'}
                </p>
              </div>
            </div>

            {/* Stripe */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
              {health?.integrations?.stripe === 'enabled' ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <AlertCircle className="text-yellow-500" size={20} />
              )}
              <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Stripe</p>
                  <p className="text-xs text-muted-foreground">
                    {health?.integrations?.stripe === 'enabled' ? 'Attivo' : 'Non configurato'}
                  </p>
                </div>
              </div>
            </div>

            {/* Resend/Email */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
              {health?.integrations?.resend === 'enabled' ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <AlertCircle className="text-yellow-500" size={20} />
              )}
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-xs text-muted-foreground">
                    {health?.integrations?.resend === 'enabled' ? 'Attivo' : 'Non configurato'}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Stats */}
            <Link to="/admin/email-logs" className="flex items-center gap-3 p-3 bg-muted/50 rounded hover:bg-muted transition">
              <div>
                <p className="text-sm font-medium">Email Logs</p>
                <p className="text-xs text-muted-foreground">
                  {emailStats ? (
                    <>
                      {emailStats.sent} inviate • 
                      <span className={emailStats.failed > 0 ? ' text-red-500' : ''}>
                        {emailStats.failed} fallite
                      </span>
                    </>
                  ) : 'Caricamento...'}
                </p>
              </div>
              <ArrowRight size={16} className="text-muted-foreground ml-auto" />
            </Link>
          </div>

          {/* Version info */}
          {health && (
            <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-4">
              <span>Versione: {health.app_version || 'N/A'}</span>
              {health.commit_hash && <span>Commit: {health.commit_hash}</span>}
              <span>Ultimo check: {new Date(health.timestamp).toLocaleTimeString('it-IT')}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
